import { cn } from '@rental/ui'
import { type ReactNode, type RefObject, type SyntheticEvent, useId } from 'react'
import { createPortal } from 'react-dom'

/** Stops a React event at the dialog: the tree around its opener never hears it. */
const stop = (event: SyntheticEvent) => event.stopPropagation()

/**
 * Every dialog in the staff app (DESIGN.md, "Staff app direction"), in a native
 * `<dialog>`: `showModal()` moves focus inside and makes the page behind inert,
 * Esc closes it, and focus goes back to what opened it.
 *
 * - It is drawn at the end of the page, outside any form. A form inside another
 *   form keeps its submit to itself and the browser loads the page again
 *   (FIX-0). Nothing done inside reaches the React tree around the button that
 *   opened it either: submits, keys, clicks, focus and changes stop here, so a
 *   row's click or a form's handler around the opener never answers for it.
 * - It is always in the page. The caller opens and closes it through
 *   `dialogRef`, and a close never unmounts what it holds, so a request in
 *   flight, its idempotency key and what was typed survive.
 * - While `busy`, Esc does not close it: a request on its way cannot be called
 *   back, and its answer, an error included, shows here. Chromium closes it
 *   anyway after repeated presses (the third, in 141); what it holds stays, and
 *   the request finishes.
 * - It is 448 px wide at most; a caller that needs more says so in `className`.
 */
export function Dialog({
  dialogRef,
  title,
  label,
  busy = false,
  onClose,
  className,
  children,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  /** Its heading, which names it. */
  title?: ReactNode
  /** Its name when it has no heading (a photograph). */
  label?: string
  busy?: boolean
  /** Every way out ends here: a button, Esc, a success. */
  onClose?: () => void
  className?: string
  children: ReactNode
}) {
  const titleId = useId()
  return createPortal(
    <dialog
      ref={dialogRef}
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : label}
      onClose={onClose}
      onCancel={(event) => {
        if (busy) event.preventDefault()
      }}
      onSubmit={stop}
      onKeyDown={stop}
      onKeyUp={stop}
      onClick={stop}
      onPointerDown={stop}
      onFocus={stop}
      onBlur={stop}
      onChange={stop}
      onInput={stop}
      className={cn(
        'staff-dialog glass-overlay m-auto w-full max-w-md rounded-lg p-6 backdrop:bg-scrim',
        className,
      )}
    >
      {title ? (
        <h2 id={titleId} className="mb-4 text-xl">
          {title}
        </h2>
      ) : null}
      {children}
    </dialog>,
    document.body,
  )
}
